/**
 * Integrated SEO Dashboard Component
 * Connects the dashboard UI with the API and manages state
 */

import { useState, useEffect } from 'react';
import { SEODashboardV2 } from './SEODashboardV2';
import { RealtimeIndicator } from './RealtimeIndicator';
import { useActions } from '../../hooks/useActions';
import { FreemiumUpgradeBanner } from '../FreemiumUpgradeBanner';
import { CitationTrackerUpsell } from '../CitationTrackerUpsell';
import PurchaseCreditsModal from '../PurchaseCreditsModal';
import { exportActionsAsMarkdown } from '../../utils/exportMarkdown';
import api from '../../services/api';

export function IntegratedDashboard({ domain, timestamp, jobId, onSubscriptionInfoChange }) {
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [filters, setFilters] = useState({
    timeAvailable: null,
    impact: [],
    category: [],
    difficulty: [],
    searchTerm: ''
  });

  const { 
    actions, 
    executiveSummary,
    stats, 
    loading, 
    error, 
    refetch, 
    updateActionStatus,
    isConnected,
    lastUpdate,
    freemiumData
  } = useActions(domain, timestamp, jobId);

  // Pass subscription info up to parent when it changes
  useEffect(() => {
    if (onSubscriptionInfoChange) {
      // freemiumData null = full access, freemiumData exists = limited access
      const hasFullAccess = freemiumData === null;
      onSubscriptionInfoChange({
        hasFullAccess,
        isFreemium: !hasFullAccess,
        freemiumData
      });
    }
  }, [freemiumData, onSubscriptionInfoChange]);

  const [currentExecutiveSummary, setCurrentExecutiveSummary] = useState(executiveSummary);

  // Update local state when executiveSummary from hook changes
  useEffect(() => {
    setCurrentExecutiveSummary(executiveSummary);
  }, [executiveSummary]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment_success") !== "true") return undefined;

    let cancelled = false;
    (async () => {
      try {
        await api.call("/subscription/verify-payment", { method: "POST" });
      } catch (error) {
        console.error("Failed to verify payment after checkout", error);
      }
      if (cancelled) return;
      params.delete("payment_success");
      const query = params.toString();
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${query ? `?${query}` : ""}`,
      );
      refetch();
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSummaryGenerated = (newSummary) => {
    setCurrentExecutiveSummary(newSummary);
  };

  const handleStatusChange = async (actionId, newStatus) => {
    await updateActionStatus(actionId, newStatus);
  };

  const handleMoveToProgress = async (actionId) => {
    await updateActionStatus(actionId, 'in-progress');
  };

  const handleExportMarkdown = () => {
    try {
      const result = exportActionsAsMarkdown(actions, domain, currentExecutiveSummary);
      console.log(`Exported ${result.actionCount} actions to ${result.filename}`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export actions. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading SEO actions...</p>
          <p className="mt-2 text-xs text-muted-foreground">Domain: {domain}, Timestamp: {timestamp}, JobId: {jobId}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="bg-card border border-red-200 rounded-lg p-6 max-w-md">
          <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Actions</h3>
          <p className="text-red-700">{error}</p>
          <button
            onClick={refetch}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Freemium Upgrade Banner */}
      {freemiumData?.isFreemiumPreview && (
        <div className="p-4 sm:p-6 lg:p-8">
          <FreemiumUpgradeBanner
            totalActions={freemiumData.totalActionsAvailable}
            shownActions={freemiumData.actionsShown}
            onUpgrade={() => setShowPurchaseModal(true)}
          />
        </div>
      )}

      {/* Main Dashboard */}
      <SEODashboardV2
        actions={actions}
        executiveSummary={currentExecutiveSummary}
        filters={filters}
        onFiltersChange={setFilters}
        onStatusChange={handleStatusChange}
        onMoveToProgress={handleMoveToProgress}
        stats={stats}
        onRefresh={refetch}
        isConnected={isConnected}
        lastUpdate={lastUpdate}
        domain={domain}
        jobId={jobId}
        onSummaryGenerated={handleSummaryGenerated}
        isFreemium={freemiumData?.isFreemiumPreview}
        onExportMarkdown={handleExportMarkdown}
      />

      <PurchaseCreditsModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
      />
    </div>
  );
}